import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { NearbyChildcareDisclaimer } from '../../../components/NearbyChildcareDisclaimer/NearbyChildcareDisclaimer';
import { NearbySchoolsDisclaimer } from '../../../components/NearbySchoolsDisclaimer/NearbySchoolsDisclaimer';
import { DemographicsDataDisclaimer } from '../../../components/DemographicsDataDisclaimer/DemographicsDataDisclaimer';
import { SuburbPageVGDisclaimerModal } from '../../components/SuburbPageVGDisclaimerModal/SuburbPageVGDisclaimerModal';

interface NearbyChildcareDisclaimerModal {
  name: 'nearbyChildcareDisclaimer';
}

interface NearbySchoolsDisclaimerModal {
  name: 'nearbySchoolsDisclaimer';
}

interface DemographicsDataDisclaimerModal {
  name: 'demographicsDataDisclaimer';
}

interface VGDisclaimerModal {
  name: 'vgDisclaimer';
}

type SuburbPageModal =
  | NearbyChildcareDisclaimerModal
  | NearbySchoolsDisclaimerModal
  | DemographicsDataDisclaimerModal
  | VGDisclaimerModal
  | null;

interface SuburbPageModalContext {
  displayModal: (x: SuburbPageModal) => void;
}

export const SuburbPageModalContext = createContext<SuburbPageModalContext | undefined>(undefined);

export const useSuburbPageModalContext = (): SuburbPageModalContext => {
  const context = useContext(SuburbPageModalContext);
  if (context === undefined) {
    throw new Error('SuburbPageModalContext must be used within a SuburbPageModalContext.Provider');
  }
  return context;
};

interface SuburbPageModalHandlerProps {
  modal: SuburbPageModal;
  onClose: () => void;
}

export const SuburbPageModalHandler: FunctionComponent<SuburbPageModalHandlerProps> = ({
  modal,
  onClose,
}) => {
  switch (modal?.name) {
    case 'nearbyChildcareDisclaimer':
      return <NearbyChildcareDisclaimer opened onClose={onClose} />;
    case 'nearbySchoolsDisclaimer':
      return <NearbySchoolsDisclaimer opened onClose={onClose} />;
    case 'demographicsDataDisclaimer':
      return <DemographicsDataDisclaimer opened onClose={onClose} />;
    case 'vgDisclaimer':
      return <SuburbPageVGDisclaimerModal opened onClose={onClose} />;
    default:
      return null;
  }
};

export const SuburbPageModalContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [modal, setModal] = useState<SuburbPageModal>(null);
  const contextValue = useMemo(
    () => ({
      displayModal: (a: SuburbPageModal) => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setModal(a);
          }, 0);
        });
      },
    }),
    [setModal],
  );

  return (
    <SuburbPageModalContext.Provider value={contextValue}>
      {modal && <SuburbPageModalHandler modal={modal} onClose={() => setModal(null)} />}
      {children}
    </SuburbPageModalContext.Provider>
  );
};
